import { useCallback, useEffect, useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Drawer,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../api/axiosInstance';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../context/AuthContext';

const TYPES = [
  { value: 'technique', label: 'Technique' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'administratif', label: 'Administratif' },
];

const emptyForm = (organizationId = '') => ({
  organizationId,
  date: new Date().toISOString().slice(0, 10),
  type: 'technique',
  change: '',
  context: '',
  impact: '',
  ticketId: '',
  documentaryUpdate: '',
});

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fr-FR') : '-');

export default function ClientActivityJournal({ embedded = false, filters = {} }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [tickets, setTickets] = useState([]);
  const [supportProjects, setSupportProjects] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const filtersKey = JSON.stringify(filters);
  const visibleRows = useMemo(() => {
    const start = filters.startDate ? String(filters.startDate).slice(0, 10) : '';
    const end = filters.endDate ? String(filters.endDate).slice(0, 10) : '';
    if (!start && !end) return rows;
    return rows.filter((row) => {
      const date = row.date ? String(row.date).slice(0, 10) : '';
      return date && (!start || date >= start) && (!end || date <= end);
    });
  }, [filters.endDate, filters.startDate, rows]);

  const clientOrganizationName = useMemo(() => {
    if (isAdmin) return '';
    return organizations.find((organization) => organization._id === selectedOrganization)?.name
      || organizations[0]?.name
      || user?.organization?.name
      || user?.organizationName
      || 'Client';
  }, [isAdmin, organizations, selectedOrganization, user]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    Promise.all([
      api.get('/api/client/activity-journal', {
        params: { organizationId: selectedOrganization || undefined, limit: 200, ...JSON.parse(filtersKey) },
      }),
      api.get('/api/client/activity-journal/options', {
        params: { organizationId: selectedOrganization || undefined },
      }),
    ]).then(([journal, options]) => {
      if (!active) return;
      setRows(journal.data?.rows || []);
      setOrganizations(options.data?.organizations || []);
      setTickets(options.data?.tickets || []);
      setSupportProjects(options.data?.supportProjects || []);
    }).catch((err) => {
      if (active) setError(err.response?.data?.error || 'Impossible de charger le journal.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [selectedOrganization, filtersKey]);

  const setField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const selectFormOrganization = (event) => {
    const organizationId = event.target.value;
    setForm((current) => ({ ...current, organizationId, ticketId: '' }));
    setSelectedOrganization(organizationId);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) await api.patch(`/api/client/activity-journal/${editingId}`, form);
      else await api.post('/api/client/activity-journal', form);
      setEditingId(null);
      setForm(emptyForm(selectedOrganization));
      setFormOpen(false);
      const response = await api.get('/api/client/activity-journal', {
        params: { organizationId: selectedOrganization || undefined, limit: 200, ...JSON.parse(filtersKey) },
      });
      setRows(response.data?.rows || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible d’enregistrer cette entrée.');
    } finally {
      setSaving(false);
    }
  };

  const edit = useCallback((row) => {
    const organizationId = row.Organization?._id || row.Organization || '';
    setSelectedOrganization(organizationId);
    setForm({
      organizationId,
      date: row.date?.slice(0, 10) || '',
      type: row.type || 'technique',
      change: row.change || '',
      context: row.context || '',
      impact: row.impact || '',
      ticketId: row.ticketId || '',
      documentaryUpdate: row.documentaryUpdate || '',
    });
    setEditingId(row._id);
    setFormOpen(true);
  }, []);

  const remove = useCallback(async (id) => {
    if (!window.confirm('Supprimer cette entrée du journal ?')) return;
    try {
      await api.delete(`/api/client/activity-journal/${id}`);
      setRows((current) => current.filter((row) => row._id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible de supprimer cette entrée.');
    }
  }, []);

  const ticketUrl = (ticketId) => `https://toolbox.easyredmine.com/issues/${ticketId}`;
  const ticketHint = selectedOrganization && !supportProjects.length
    ? 'Aucun support EasyRedmine n’est rattaché à ce client.'
    : `${tickets.length} ticket(s) disponible(s) pour les supports du client`;

  const columns = useMemo(() => [
    {
      accessorKey: 'date',
      header: 'Date',
      size: 120,
      filterVariant: 'date',
      accessorFn: (row) => row.date ? row.date.slice(0, 10) : '',
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      id: 'client',
      header: 'Client',
      size: 170,
      accessorFn: (row) => row.Organization?.name || '-',
      filterVariant: 'autocomplete',
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 140,
      filterVariant: 'select',
      filterSelectOptions: TYPES.map((item) => ({ text: item.label, value: item.value })),
      Cell: ({ cell }) => {
        const type = cell.getValue();
        return <Chip
          size="small"
          variant="filled"
          color={type === 'commercial' ? 'warning' : type === 'administratif' ? 'info' : 'success'}
          sx={type === 'administratif' ? { color: 'info.contrastText', backgroundColor: 'info.main' } : undefined}
          label={TYPES.find((item) => item.value === type)?.label || 'Technique'}
        />;
      },
    },
    {
      accessorKey: 'change',
      header: 'Changement',
      minSize: 240,
      size: 320,
      muiTableBodyCellProps: { sx: { whiteSpace: 'pre-wrap' } },
    },
    {
      accessorKey: 'context',
      header: 'Contexte / décision',
      minSize: 220,
      size: 280,
      Cell: ({ cell }) => cell.getValue() || '-',
      muiTableBodyCellProps: { sx: { whiteSpace: 'pre-wrap' } },
    },
    {
      accessorKey: 'impact',
      header: 'Impact',
      minSize: 220,
      size: 280,
      Cell: ({ cell }) => cell.getValue() || '-',
      muiTableBodyCellProps: { sx: { whiteSpace: 'pre-wrap' } },
    },
    {
      id: 'ticket',
      header: 'Ticket / projet',
      size: 150,
      accessorFn: (row) => row.ticketId ? `#${row.ticketId}` : '-',
      Cell: ({ row }) => row.original.ticketId ? (
        <Chip component="a" clickable href={ticketUrl(row.original.ticketId)} target="_blank" rel="noreferrer" size="small" color="primary" variant="outlined" icon={<OpenInNewIcon />} label={`#${row.original.ticketId}`} />
      ) : '-',
    },
    {
      accessorKey: 'authorName',
      header: 'Auteur',
      size: 170,
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'documentaryUpdate',
      header: 'Mise à jour documentaire',
      minSize: 220,
      size: 260,
      Cell: ({ cell }) => cell.getValue() || '-',
      muiTableBodyCellProps: { sx: { whiteSpace: 'pre-wrap' } },
    },
    ...(isAdmin ? [{
      id: 'actions',
      header: 'Actions',
      size: 100,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0}>
          <IconButton size="small" onClick={() => edit(row.original)} aria-label="Modifier"><EditOutlinedIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => remove(row.original._id)} aria-label="Supprimer"><DeleteOutlineIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    }] : []),
  ], [isAdmin, edit, remove]);

  const table = useMaterialReactTable({
    columns,
    data: visibleRows,
    localization: MRT_Localization_FR,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableSorting: true,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageIndex: 0, pageSize: 10 },
      ...(isAdmin ? { columnPinning: { right: ['actions'] } } : {}),
    },
    state: { isLoading: loading },
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
    muiPaginationProps: { rowsPerPageOptions: [10, 25, 50, 100], showFirstButton: true, showLastButton: true },
    positionToolbarAlertBanner: 'bottom',
  });

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {!embedded && <PageHeader section={isAdmin ? 'Clients' : clientOrganizationName} title="Journal d’activité" />}
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Historique des changements réalisés.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isAdmin && (
        <Drawer anchor="right" open={formOpen} onClose={() => setFormOpen(false)}>
          <Paper component="form" onSubmit={submit} variant="outlined" square elevation={0} sx={{ width: { xs: '100vw', sm: 860, lg: 980 }, maxWidth: '100vw', height: '100%', overflowY: 'auto', p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={800}>
            {editingId ? 'Modifier une entrée' : 'Ajouter une activité'}
              </Typography>
              <IconButton type="button" onClick={() => setFormOpen(false)} aria-label="Fermer"><CloseIcon /></IconButton>
            </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1.5 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Autocomplete fullWidth size="small" options={organizations} value={organizations.find((item) => item._id === form.organizationId) || null} onChange={(event, value) => selectFormOrganization({ target: { value: value?._id || '' } })} getOptionLabel={(option) => option.name || ''} isOptionEqualToValue={(option, value) => option._id === value._id} renderInput={(params) => <TextField {...params} label="Client" required />} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.date} onChange={setField('date')} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Autocomplete fullWidth size="small" options={TYPES} value={TYPES.find((item) => item.value === form.type) || null} onChange={(event, value) => setForm((current) => ({ ...current, type: value?.value || 'technique' }))} getOptionLabel={(option) => option.label} renderInput={(params) => <TextField {...params} label="Type" required />} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth required label="Changement" value={form.change} onChange={setField('change')} multiline minRows={2} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth label="Contexte / décision" value={form.context} onChange={setField('context')} multiline minRows={2} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth label="Impact" value={form.impact} onChange={setField('impact')} multiline minRows={2} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Autocomplete fullWidth size="small" disabled={!form.organizationId || !supportProjects.length} options={tickets} value={tickets.find((ticket) => String(ticket.ticketId) === String(form.ticketId)) || null} onChange={(event, value) => setForm((current) => ({ ...current, ticketId: value?.ticketId || '' }))} getOptionLabel={(option) => `#${option.ticketId} - ${option.subject || 'Sans objet'}`} isOptionEqualToValue={(option, value) => option.ticketId === value.ticketId} renderInput={(params) => <TextField {...params} label="Ticket EasyRedmine" />} />
              <Typography variant="caption" color="text.secondary">{ticketHint}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth label="Mise à jour documentaire" value={form.documentaryUpdate} onChange={setField('documentaryUpdate')} multiline minRows={2} />
            </Box>
          </Box>
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" color="success" disabled={saving || !form.organizationId}>{editingId ? 'Enregistrer' : 'Ajouter au journal'}</Button>
            <Button type="button" onClick={() => { setEditingId(null); setForm(emptyForm(selectedOrganization)); setFormOpen(false); }}>Annuler</Button>
          </Stack>
          </Paper>
        </Drawer>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 1 }}>
        <HistoryIcon color="success" />
        <Typography variant="h6" fontWeight={800}>Journal</Typography>
        {isAdmin && <Autocomplete size="small" sx={{ minWidth: 260 }} options={organizations} value={organizations.find((item) => item._id === selectedOrganization) || null} onChange={(event, value) => setSelectedOrganization(value?._id || '')} getOptionLabel={(option) => option.name || ''} isOptionEqualToValue={(option, value) => option._id === value._id} renderInput={(params) => <TextField {...params} label="Filtrer par client" placeholder="Tous les clients" />} />}
        <Chip label={`${visibleRows.length} entrée(s)`} />
        {isAdmin && <Button variant="contained" color="success" onClick={() => { setEditingId(null); setForm(emptyForm(selectedOrganization)); setFormOpen(true); }}>Ajouter une activité</Button>}
      </Stack>

      <Box sx={{ overflowX: 'auto' }}>
        <MaterialReactTable table={table} />
      </Box>
    </Box>
  );
}
